
import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import EditProfileModal from './EditProfileModal';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  userProfile: any;
  userType: string;
  isAuthReady: boolean;
  setIsUpgradeModalOpen: (open: boolean) => void;
  setIsSettingsModalOpen: (open: boolean) => void;
  setIsSupportModalOpen: (open: boolean) => void;
  setIsSignOutModalOpen: (open: boolean) => void;
  setSettingsInitialView: (view: string | null) => void;
  isTechnicalSuiteOpen?: boolean;
  setIsTechnicalSuiteOpen?: (open: boolean) => void;
  onPrivacySettingsClick?: () => void;
  onOpenSavedOpportunities?: () => void;
  savedCount?: number;
}

const Sidebar: React.FC<SidebarProps> = memo(({
  isOpen,
  onClose,
  user,
  userProfile,
  userType,
  isAuthReady,
  setIsUpgradeModalOpen,
  setIsSettingsModalOpen,
  setIsSupportModalOpen,
  setIsSignOutModalOpen,
  setSettingsInitialView,
  isTechnicalSuiteOpen,
  setIsTechnicalSuiteOpen,
  onPrivacySettingsClick,
  onOpenSavedOpportunities,
  savedCount = 0
}) => {
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);

  const isPremiumOrAbove = user && ['premium', 'optimize', 'team', 'enterprise', 'premium-pro', 'premium_pro', 'optimize_pro', 'optimize_basic'].includes(userProfile?.subscriptionTier);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/75 z-[250]"
            />
            <motion.div
              key="sidebar-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-80 bg-[#3a3430] border-l border-white/[0.06] z-[300] flex flex-col text-white"
            >
              {!isAuthReady ? (
                <div className="flex flex-col h-full bg-[#3a3430]">
                  <div className="p-6 border-b border-white/[0.06] bg-[#322D29] shrink-0">
                    <div className="flex items-center justify-between mb-6">
                      <div className="h-3 w-24 bg-white/10 rounded animate-pulse"></div>
                      <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                        <i className="fas fa-times text-base"></i>
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/10 rounded-lg animate-pulse"></div>
                      <div className="flex flex-col gap-2">
                        <div className="h-4 w-32 bg-white/10 rounded animate-pulse"></div>
                        <div className="h-2 w-20 bg-white/10 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 p-4 space-y-3">
                    {[1, 2, 3, 4].map(i => (
                      <div key={`skeleton-${i}`} className="h-10 w-full bg-white/5 rounded-lg animate-pulse"></div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-6 border-b border-white/[0.06] bg-[#322D29] shrink-0">
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40 font-heading">Operative Profile</span>
                      <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                        <i className="fas fa-times text-base"></i>
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-3.5">
                      <div className="w-14 h-14 bg-[#322D29] border border-white/10 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                        {userProfile?.photoURL || user?.photoURL ? (
                          <img src={userProfile?.photoURL || user?.photoURL} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <i className="fas fa-user-secret text-white/40 text-xl"></i>
                        )}
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-sm font-bold uppercase tracking-tight text-white leading-tight mb-1 truncate font-heading">
                          {userProfile?.name || user?.displayName || (userType === 'company' ? 'Enterprise User' : 'Individual User')}
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#F26522] font-heading">
                          {userType === 'company' ? 'ENTERPRISE ACCOUNT' : 'INDIVIDUAL ACCOUNT'}
                        </span>
                        {userProfile?.subscriptionTier === 'optimize_pro' && (
                          <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#F26522] mt-0.5 flex items-center gap-1 font-heading">
                            <i className="fas fa-gauge-high"></i>
                            Optimize Pro
                          </span>
                        )}
                        <span className="text-[9px] font-medium text-white/40 mt-1 truncate">{user?.email}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 space-y-1 no-scrollbar bg-[#3a3430]">
                    <button 
                      key="menu-edit-profile"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsEditProfileModalOpen(true);
                        onClose();
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                    >
                      <div className="flex items-center gap-3.5">
                        <i className="fas fa-user-edit text-xs text-white/40 group-hover:text-[#F26522] transition-colors"></i>
                        <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Edit Profile</span>
                      </div>
                      <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                    </button>

                    {onOpenSavedOpportunities && (
                      <button 
                        key="menu-saved-opportunities"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenSavedOpportunities();
                          onClose();
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                        id="sidebar-access-saved-btn"
                      >
                        <div className="flex items-center gap-3.5">
                          <i className="fas fa-bookmark text-xs text-[#F26522]"></i>
                          <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Access Saved Opportunities</span>
                        </div>
                        {savedCount > 0 ? (
                          <span className="bg-[#F26522] text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                            {savedCount}
                          </span>
                        ) : (
                          <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                        )}
                      </button>
                    )}

                  <button 
                    key="menu-upgrade"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsUpgradeModalOpen(true);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white rounded-lg transition-all group cursor-pointer font-heading"
                  >
                    <div className="flex items-center gap-3.5">
                      <i className="fas fa-crown text-xs"></i>
                      <span className="text-[10px] font-bold uppercase tracking-widest">Upgrade Plan</span>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/60 group-hover:text-white transition-all"></i>
                  </button>

                  <div className="h-px bg-white/[0.06] my-2 mx-2"></div>

                  {isPremiumOrAbove && (
                    <button 
                      key="menu-technical-suite"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsTechnicalSuiteOpen?.(true);
                        onClose();
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                    >
                      <div className="flex items-center gap-3.5">
                        <i className="fas fa-sliders-h text-xs text-[#F26522]"></i>
                        <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Technical Suite</span>
                      </div>
                      <span className="text-[8px] font-bold text-[#F26522] bg-[#F26522]/15 px-1.5 py-0.5 rounded border border-[#F26522]/30 tracking-wider">A/B Ready</span>
                    </button>
                  )}

                  <button 
                    key="menu-settings"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSettingsModalOpen(true);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <i className="fas fa-cog text-xs text-white/40 group-hover:text-[#F26522] transition-colors"></i>
                      <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Settings</span>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                  </button>

                  <button 
                    key="menu-support"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSupportModalOpen(true);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <i className="fas fa-headset text-xs text-white/40 group-hover:text-[#F26522] transition-colors"></i>
                      <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Support</span>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                  </button>

                  <button 
                    key="menu-privacy"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSettingsInitialView('policy');
                      setIsSettingsModalOpen(true);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <i className="fas fa-shield-alt text-xs text-white/40 group-hover:text-[#F26522] transition-colors"></i>
                      <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Privacy Policy</span>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                  </button>

                  <button 
                    key="menu-privacy-settings"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onPrivacySettingsClick) {
                        onPrivacySettingsClick();
                      }
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-white/80 hover:text-white rounded-lg transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <i className="fas fa-lock text-xs text-white/40 group-hover:text-[#F26522] transition-colors"></i>
                      <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Privacy Settings</span>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-white/20 group-hover:text-white/60 transition-all"></i>
                  </button>
                </div>

                <div className="p-4 border-t border-white/[0.06] bg-[#322D29]">
                  <button 
                    key="menu-signout"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSignOutModalOpen(true);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-950/30 text-red-400 hover:text-red-300 rounded-lg transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <i className="fas fa-power-off text-xs text-red-400"></i>
                      <span className="text-[10px] font-bold uppercase tracking-widest font-heading">Terminate Session</span>
                    </div>
                    <i className="fas fa-chevron-right text-[9px] text-red-400/40 group-hover:text-red-400 transition-all"></i>
                  </button>
                </div>
              </>
              )}
            </motion.div>
        </>
      )}
    </AnimatePresence>

    {user && (
      <EditProfileModal
        isOpen={isEditProfileModalOpen}
        onClose={() => setIsEditProfileModalOpen(false)}
        onUpgradeClick={() => {
          setIsEditProfileModalOpen(false);
          setIsUpgradeModalOpen(true);
        }}
        userProfile={userProfile}
        userId={user.uid}
      />
    )}
  </>
);
}, (prevProps, nextProps) => {
  // Only re-render if user ID changes or open state changes or auth ready state changes
  return prevProps.isOpen === nextProps.isOpen && 
         prevProps.isAuthReady === nextProps.isAuthReady &&
         prevProps.user?.uid === nextProps.user?.uid &&
         prevProps.userProfile?.updatedAt === nextProps.userProfile?.updatedAt;
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
